import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Spinner from "../components/Shared/Spinner";
import CredentialLibrary from "../components/Roadmap/CredentialLibrary";
import RoadmapHero from "../components/Roadmap/RoadmapHero";
import RoadmapQuestionnaire from "../components/Roadmap/RoadmapQuestionnaire";
import RoadmapResult from "../components/Roadmap/RoadmapResult";
import {
  emptyAnswers,
  type AnswerMap,
  type RoadmapLanguage,
  type RoadmapView,
  type StepKey,
} from "../components/Roadmap/types";
import { GetQuestionnaire } from "../api/CertRoadmap/GetQuestionnaire";
import { GetCredentials } from "../api/CertRoadmap/GetCredentials";
import { GetMyRoadmap } from "../api/CertRoadmap/GetMyRoadmap";
import { SubmitRoadmap } from "../api/CertRoadmap/SubmitRoadmap";
import { UpdateCredentialProgress } from "../api/CertRoadmap/UpdateProgress";
import type {
  ProgressStatus,
  QuestionOption,
  RoadmapSubmitPayload,
} from "../api/CertRoadmap/types";
import type { ApiError } from "../types/ApiError";
import { getAccessToken } from "../lib/session";
import { queryClient } from "../main";

const initialRoadmapView = (): RoadmapView => {
  const params = new URLSearchParams(window.location.search);
  return params.has("category") ||
    params.has("difficulty") ||
    params.has("sort")
    ? "discover"
    : "quiz";
};

const Roadmap = () => {
  const loggedIn = !!getAccessToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const language: RoadmapLanguage =
    searchParams.get("lang") === "ar" ? "ar" : "en";
  const [activeView, setActiveView] =
    useState<RoadmapView>(initialRoadmapView);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>(emptyAnswers);
  const [retaking, setRetaking] = useState(false);
  const questionRef = useRef<HTMLElement>(null);
  const shouldPositionQuestion = useRef(false);
  const shouldPositionResult = useRef(false);

  const changeLanguage = (nextLanguage: RoadmapLanguage) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextLanguage === "ar") {
      nextParams.set("lang", "ar");
    } else {
      nextParams.delete("lang");
    }
    setSearchParams(nextParams, { replace: true });
  };

  useLayoutEffect(() => {
    if (!shouldPositionQuestion.current) return;

    shouldPositionQuestion.current = false;
    if (
      !questionRef.current ||
      window.matchMedia("(min-width: 768px)").matches
    ) {
      return;
    }

    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    questionRef.current.scrollIntoView({ block: "start" });
    root.style.scrollBehavior = previousScrollBehavior;
  }, [stepIndex]);

  const {
    data: questionnaireRes,
    isLoading: questionnaireLoading,
    error: questionnaireError,
  } = useQuery({
    queryKey: ["cert-roadmap-questionnaire"],
    queryFn: GetQuestionnaire,
    staleTime: 5 * 60 * 1000,
    enabled: loggedIn,
  });

  const { data: credentialsRes, isLoading: credentialsLoading } = useQuery({
    queryKey: ["cert-roadmap-credentials"],
    queryFn: GetCredentials,
    staleTime: 5 * 60 * 1000,
    enabled: loggedIn,
  });

  const {
    data: myRoadmapRes,
    isLoading: myRoadmapLoading,
    refetch: refetchMyRoadmap,
  } = useQuery({
    queryKey: ["cert-roadmap-me"],
    queryFn: GetMyRoadmap,
    enabled: loggedIn,
    staleTime: 0,
    retry: false,
  });

  const questionnaire = questionnaireRes?.data;
  const credentials = useMemo(
    () => credentialsRes?.data ?? [],
    [credentialsRes?.data]
  );
  const myRoadmap = myRoadmapRes?.data ?? null;
  const specializationOptionsByTrack =
    questionnaire?.specialization.options_by_track ?? {};
  const trackForSpecialization = (specializationId: string) =>
    Object.entries(specializationOptionsByTrack).find(([, options]) =>
      options.some((option) => option.id === specializationId)
    )?.[0];

  const needsWorkSystem =
    answers.specialization.some((specialization) =>
      questionnaire?.work_system.required_when_specialization_in?.includes(
        specialization
      )
    );

  const steps = useMemo(() => {
    const list: StepKey[] = [
      "career_stage",
      "career_track",
      "specialization",
      "secondary_needs",
    ];

    if (needsWorkSystem) list.push("work_system");
    list.push(
      "foundation_gaps",
      "qualification",
      "eligibility_evidence",
      "completed_credentials"
    );
    return list;
  }, [needsWorkSystem]);

  const currentStep = steps[Math.min(stepIndex, steps.length - 1)];

  const currentOptions: QuestionOption[] = useMemo(() => {
    if (!questionnaire) return [];

    if (currentStep === "completed_credentials") {
      return credentials.map((credential) => ({
        id: credential.id,
        label: {
          en: `${credential.code} — ${credential.name.en}`,
          ar: `${credential.code} — ${credential.name.ar}`,
        },
      }));
    }

    if (currentStep === "specialization") {
      const seen = new Set<string>();
      return answers.career_track
        .flatMap(
          (track) =>
            questionnaire.specialization.options_by_track?.[track] ?? []
        )
        .filter((option) => {
          if (seen.has(option.id)) return false;
          seen.add(option.id);
          return true;
        });
    }

    return questionnaire[currentStep]?.options ?? [];
  }, [answers.career_track, credentials, currentStep, questionnaire]);

  const currentLabel = useMemo(() => {
    if (currentStep === "completed_credentials") {
      return {
        en: "Which credentials have you already completed? (optional)",
        ar: "ما الشهادات التي أكملتها بالفعل؟ (اختياري)",
      };
    }
    return questionnaire?.[currentStep]?.label;
  }, [currentStep, questionnaire]);

  const currentType =
    currentStep === "completed_credentials" ||
    currentStep === "career_track" ||
    currentStep === "specialization"
      ? "multi"
      : questionnaire?.[currentStep]?.type ?? "single";

  const isStepAnswered = () => {
    if (!questionnaire) return false;

    const question = questionnaire[currentStep as keyof typeof questionnaire];
    const required =
      currentStep === "completed_credentials"
        ? false
        : currentStep === "work_system"
          ? needsWorkSystem
          : currentStep === "secondary_needs" ||
              currentStep === "eligibility_evidence"
            ? false
            : !!question?.required;

    if (currentStep === "career_track") {
      return answers.career_track.length > 0;
    }

    if (currentStep === "specialization") {
      return (
        answers.specialization.length === answers.career_track.length &&
        answers.specialization.every((specialization) => {
          const track = trackForSpecialization(specialization);
          return !!track && answers.career_track.includes(track);
        }) &&
        answers.career_track.every((track) =>
          (specializationOptionsByTrack[track] ?? []).some((option) =>
            answers.specialization.includes(option.id)
          )
        )
      );
    }

    if (!required) return true;

    if (currentType === "multi") {
      const value = answers[currentStep] as string[] | undefined;
      return Array.isArray(value) && value.length > 0;
    }

    return !!answers[currentStep as keyof AnswerMap];
  };

  const selectSingle = (id: string) => {
    setAnswers((previous) => {
      const key = currentStep as
        | "career_stage"
        | "work_system"
        | "qualification";
      return { ...previous, [key]: id };
    });
  };

  const toggleMulti = (id: string) => {
    if (currentStep === "career_track") {
      const currentlySelected = answers.career_track.includes(id);
      const nextTracks = currentlySelected
        ? answers.career_track.filter((track) => track !== id)
        : [...answers.career_track, id];

      if (
        nextTracks.length > 1 &&
        (nextTracks.length > 2 || !nextTracks.includes("leadership"))
      ) {
        toast.error(
          language === "ar"
            ? 'يمكن دمج "إدارة المشروعات أو القيادة التنفيذية" فقط مع مسار مهني آخر.'
            : 'Only "Project management or executive leadership" can be combined with another career track.'
        );
        return;
      }

      setAnswers((previous) => ({
        ...previous,
        career_track: nextTracks,
        specialization: previous.specialization.filter((specialization) => {
          const track = trackForSpecialization(specialization);
          return !!track && nextTracks.includes(track);
        }),
        work_system: undefined,
      }));
      return;
    }

    if (currentStep === "specialization") {
      const track = trackForSpecialization(id);
      if (!track || !answers.career_track.includes(track)) {
        toast.error(
          language === "ar"
            ? "هذا التخصص لا ينتمي إلى المسارات المهنية المختارة."
            : "This specialization does not belong to the selected career tracks."
        );
        return;
      }

      setAnswers((previous) => {
        const alreadySelected = previous.specialization.includes(id);
        const withoutTrackSpecialization = previous.specialization.filter(
          (specialization) =>
            trackForSpecialization(specialization) !== track
        );

        return {
          ...previous,
          specialization: alreadySelected
            ? withoutTrackSpecialization
            : [...withoutTrackSpecialization, id],
          work_system: undefined,
        };
      });
      return;
    }

    const exclusive =
      currentStep === "foundation_gaps"
        ? questionnaire?.foundation_gaps.mutually_exclusive_option
        : currentStep === "secondary_needs"
          ? "none"
          : undefined;

    setAnswers((previous) => {
      const key = currentStep as
        | "secondary_needs"
        | "foundation_gaps"
        | "eligibility_evidence"
        | "completed_credentials";
      const current = [...(previous[key] || [])];
      let updated: string[];

      if (exclusive && id === exclusive) {
        updated = current.includes(id) ? [] : [id];
      } else if (exclusive && current.includes(exclusive)) {
        updated = [id];
      } else if (current.includes(id)) {
        updated = current.filter((item) => item !== id);
      } else {
        updated = [...current, id];
      }

      return { ...previous, [key]: updated };
    });
  };

  const { mutate: submitRoadmap, isPending: isSubmitting } = useMutation({
    mutationFn: (payload: RoadmapSubmitPayload) =>
      SubmitRoadmap(payload, language),
    onSuccess: async () => {
      shouldPositionResult.current = true;
      setRetaking(false);
      await queryClient.invalidateQueries({ queryKey: ["cert-roadmap-me"] });
      await refetchMyRoadmap();
      toast.success(
        language === "ar" ? "تم إنشاء الخارطة المهنية" : "Roadmap generated"
      );
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      toast.error(
        apiError.message ||
          (language === "ar"
            ? "تعذر إنشاء الخارطة المهنية"
            : "Could not generate roadmap")
      );
    },
  });

  const { mutate: updateProgress, variables } = useMutation({
    mutationFn: ({
      credentialId,
      status,
    }: {
      credentialId: string;
      status: ProgressStatus;
    }) => UpdateCredentialProgress(credentialId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cert-roadmap-me"] });
      toast.success(
        language === "ar" ? "تم تحديث التقدم" : "Progress updated"
      );
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      toast.error(
        apiError.message ||
          (language === "ar"
            ? "تعذر تحديث التقدم"
            : "Could not update progress")
      );
    },
  });

  const buildPayload = (): RoadmapSubmitPayload => {
    const payload: RoadmapSubmitPayload = {
      career_stage: answers.career_stage || "",
      career_track: answers.career_track,
      specialization: answers.specialization,
      foundation_gaps: answers.foundation_gaps,
    };

    if (answers.secondary_needs.length) {
      payload.secondary_needs = answers.secondary_needs;
    }
    if (needsWorkSystem && answers.work_system) {
      payload.work_system = answers.work_system;
    }
    if (answers.eligibility_evidence.length) {
      payload.eligibility_evidence = answers.eligibility_evidence;
    }
    if (answers.completed_credentials.length) {
      payload.completed_credentials = answers.completed_credentials;
    }
    if (answers.qualification) {
      payload.qualification = answers.qualification;
    }

    return payload;
  };

  const goNext = () => {
    if (!isStepAnswered()) return;

    if (stepIndex >= steps.length - 1) {
      submitRoadmap(buildPayload());
      return;
    }

    shouldPositionQuestion.current = true;
    setStepIndex((index) => index + 1);
  };

  const startRetake = () => {
    setRetaking(true);
    setStepIndex(0);
    setAnswers(emptyAnswers());
  };

  const selectedValue = answers[currentStep as keyof AnswerMap];
  const selectedIds = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue
      ? [selectedValue as string]
      : [];

  const isLoadingPage =
    questionnaireLoading || (myRoadmapLoading && !retaking);
  const inResultView = !!myRoadmap?.roadmap && !retaking;

  useLayoutEffect(() => {
    if (
      !shouldPositionResult.current ||
      !inResultView ||
      !questionRef.current
    ) {
      return;
    }

    shouldPositionResult.current = false;
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    questionRef.current.scrollIntoView({ block: "start" });
    root.style.scrollBehavior = previousScrollBehavior;
  }, [inResultView]);

  if (!loggedIn) {
    return <Navigate to="/login?redirect=/roadmap" replace />;
  }

  return (
    <main
      className="overflow-hidden bg-[#f8faf9] pb-20 text-textPrimary"
      lang={language}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <RoadmapHero
        activeView={activeView}
        hasRoadmap={inResultView}
        language={language}
        onViewChange={setActiveView}
        onLanguageChange={changeLanguage}
      />

      {activeView === "quiz" ? (
        <section
          ref={questionRef}
          className="container relative z-10 mt-8 scroll-mt-24"
        >
          <div className="rounded-[28px] border border-black/5 bg-white p-5 shadow-[0_25px_70px_rgba(12,48,82,0.12)] sm:p-10">
            {isLoadingPage ? (
              <div className="flex h-64 items-center justify-center">
                <Spinner />
              </div>
            ) : questionnaireError || questionnaireRes?.error ? (
              <p className="text-center text-red-500">
                {questionnaireRes?.error?.message ||
                  (language === "ar"
                    ? "تعذر تحميل الاستبيان."
                    : "Could not load the questionnaire.")}
              </p>
            ) : inResultView && myRoadmap?.roadmap ? (
              <RoadmapResult
                data={myRoadmap}
                language={language}
                questionnaire={questionnaire}
                onRetake={startRetake}
                updatingId={variables?.credentialId}
                onUpdateProgress={(credentialId, status) =>
                  updateProgress({ credentialId, status })
                }
              />
            ) : !questionnaire ? (
              <p className="text-center text-lightGray">
                {language === "ar"
                  ? "الاستبيان غير متاح حاليًا."
                  : "Questionnaire unavailable."}
              </p>
            ) : (
              <RoadmapQuestionnaire
                stepIndex={stepIndex}
                totalSteps={steps.length}
                currentStep={currentStep}
                currentLabel={currentLabel}
                currentOptions={currentOptions}
                selectedIds={selectedIds}
                multiSelect={currentType === "multi"}
                requiredSelectionCount={
                  currentStep === "specialization"
                    ? answers.career_track.length
                    : undefined
                }
                isSubmitting={isSubmitting}
                canContinue={isStepAnswered()}
                language={language}
                onSelect={(id) =>
                  currentType === "multi"
                    ? toggleMulti(id)
                    : selectSingle(id)
                }
                onBack={() => setStepIndex((index) => Math.max(0, index - 1))}
                onNext={goNext}
              />
            )}
          </div>
        </section>
      ) : (
        <CredentialLibrary
          credentials={credentials}
          isLoading={credentialsLoading}
          language={language}
        />
      )}
    </main>
  );
};

export default Roadmap;
